import React, { useRef, useState } from 'react';
import { RootState } from '../../../reducers';
import { bindActionCreators, Dispatch } from 'redux';
import * as actions from '../../../actions';
import * as _actionsJourney1 from '../../../actions/journey1';
import * as actionsJourney2 from '../../../actions/journey2';
import * as actionsJourney3 from '../../../actions/journey3';
import * as _actionsBasket from '../../../actions/basket';
import * as _actionsSearch from '../../../actions/search';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Spinner from '../../Spinner';
import './SearchImport.scss';
import { FormattedMessage } from 'react-intl';
import { Button, EButtonType, Input, EInputType, DragDropFile } from '@repo/apg-ui';
import { ReactComponent as RedxIcon } from '../../../assets/icons/redX.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg';
import { ReactComponent as ThumbsUpIcon } from '../../../assets/icons/new/thumbs_up.svg';
import { ReactComponent as WarningIcon } from '../../../assets/icons/new/warning.svg';
import { InfoPopup } from './InfoPopup';
import { useClickOutside } from '../../../utils/useClickOutside';
import { useNavigate } from 'react-router';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

interface Props {
  history: any;
  intl: any;
  userDataEC: PartiallyIntegratedUserDataEC;
  actionsSearch: typeof _actionsSearch;
  actionsJourney1: typeof _actionsJourney1;
}

const STEPS = {
  FileUpload: 0,
  InvalidIds: 1,
  InvalidFile: 2,
  Success: 3,
};

const SearchImportModal = ({
  userDataEC: {
    ui: { isMobile },
  },
  intl,
  actionsSearch,
  actionsJourney1,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInfoModalShown, setIsInfoModalShown] = useState(false);
  const [file, setFile] = useState<File>();
  const [step, setStep] = useState<number>(STEPS.FileUpload);
  const [idsRaw, setIdsRaw] = useState<string>('');
  const [uploadedCounter, setUploadedCounter] = useState<number>(0);
  const infoModalRef = useRef(null);

  useClickOutside(infoModalRef, () => setIsInfoModalShown(false), 'info-modal-trigger');

  const navigate = useNavigate();

  const getIdsLength = (ids) => {
    return ids
      .trim()
      .split(/\s*,\s*|\s*;\s*|\s/)
      .filter((string) => string?.trim().length).length;
  };

  const searchIds = (formData: any) => {
    actionsSearch
      .postImportResults(formData)
      .then((res) => {
        for (const item of res.data.urnlist) {
          const itemId = item.urn.split(':')[2];
          actionsJourney1.addLocation({
            address: `${itemId}`,
            lngLtd: {
              lat: item.latitude,
              lng: item.langitude,
            },
            types: ['product_id', 'locality', 'imported'],
            product: {
              id: item.urn,
            },
            context: 'id',
          });
        }
        if (res.data.urnlist.length === 0) {
          setStep(STEPS.InvalidFile);
          setIsLoading(false);
        } else {
          setStep(STEPS.Success);
          setUploadedCounter(res.data.urnlist.length);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setStep(STEPS.InvalidFile);
        setIsLoading(false);
      });
  };

  const processIds = () => {
    setIsLoading(true);
    const formData = new FormData();

    if (file) {
      file.text().then((content) => {
        if (getIdsLength(content) > 250) {
          setStep(STEPS.InvalidIds);
          setIsLoading(false);
          return;
        } else {
          formData.append('file', file);
          searchIds(formData);
        }
      });
    } else if (idsRaw) {
      if (getIdsLength(idsRaw) > 250) {
        setStep(STEPS.InvalidIds);
        setIsLoading(false);
        return;
      } else {
        formData.append('searchterm', idsRaw);
        searchIds(formData);
      }
    }
  };

  const reset = () => {
    setFile(undefined);
    setStep(STEPS.FileUpload);
    setIdsRaw('');
    setIsInfoModalShown(false);
  };

  const getNextStepButton = () => {
    let actionFunc;
    let buttonLabel;
    let isDisabledCondition;

    if (step === STEPS.FileUpload) {
      actionFunc = processIds;
      isDisabledCondition = !file && !idsRaw;
      const buttonLabelDefault = (
        <FormattedMessage
          id={'ec.modals.import.uploadOrEnterIds'}
          defaultMessage={"Hochladen oder Produkt-ID's eingeben"}
        />
      );
      const buttonLabelProcess = (
        <FormattedMessage id={'ec.modals.import.processIds'} defaultMessage={"Produkt-ID's verarbeiten"} />
      );
      buttonLabel = isDisabledCondition ? buttonLabelDefault : buttonLabelProcess;
    } else if (step === STEPS.InvalidIds) {
      actionFunc = () => {
        setFile(undefined);
        setStep(STEPS.FileUpload);
        setIsInfoModalShown(false);
      };
      buttonLabel = <FormattedMessage id='ec.modals.import.continue' defaultMessage='Weiter' />;
      isDisabledCondition = false;
    } else if (step === STEPS.InvalidFile) {
      actionFunc = reset;
      buttonLabel = (
        <FormattedMessage id='ec.modals.import.reupload' defaultMessage='Laden Sie Ihre Datei erneut hoch' />
      );
      isDisabledCondition = false;
    } else if (step === STEPS.Success) {
      actionFunc = () => {
        reset();
        navigate({ search: '' });
      };
      buttonLabel = <FormattedMessage id='ec.modals.import.closeWindow' defaultMessage='Fenster schliessen' />;
      isDisabledCondition = false;
    }
    return (
      <Button
        buttonType={EButtonType.InvertedPillMedium}
        className={classNames('mt-0', {
          'w-100': step === STEPS.Success,
        })}
        isDisabled={isDisabledCondition}
        onClick={actionFunc}
      >
        {buttonLabel}
      </Button>
    );
  };

  const getInfoModal = () => (
    <>
      <div className=''>
        <Button
          buttonType={EButtonType.BasicLink}
          className={'d-flex mb-5 flex-row justify-content-center'}
          onClick={() => {
            setIsInfoModalShown(true);
          }}
        >
          <div className='text-underline font-bold  info-modal-trigger'>
            <FormattedMessage id='ec.modals.import.infoMessage.button' defaultMessage='Beispiel csv-Formatierung' />
          </div>
          <InfoIcon className={'me-3 info-icon'} />
        </Button>
      </div>
      {isInfoModalShown && (
        <div className='inner-popup-wrapper' ref={infoModalRef}>
          <div
            className='info-popup-backdrop'
            onClick={() => {
              setIsInfoModalShown(false);
            }}
          />
          <InfoPopup
            onClose={() => {
              setIsInfoModalShown(false);
            }}
          />
        </div>
      )}
    </>
  );

  const getStepContent = () => {
    switch (step) {
      case STEPS.FileUpload:
        return (
          <React.Fragment>
            <DragDropFile
              handleFile={(file) => setFile(file)}
              onRemove={() => setFile(undefined)}
              buttonLabel={intl.formatMessage({
                id: 'ec.modals.import.uploadFile',
                defaultMessage: 'Datei auswählen',
              })}
              inputLabel={intl.formatMessage({
                id: 'ec.modals.import.uploadFileDescription',
                defaultMessage: 'Drag & drop eine .csv Datei hier',
              })}
              fileFormat='.csv'
              file={file}
              children={getInfoModal()}
            />
            <div className='font-20 font-bold mt-48'>
              <FormattedMessage
                id='ec.modals.import.manualInput'
                defaultMessage="oder geben Sie hier die Produkt-ID's manuell ein"
              />
            </div>
            <Input
              inputType={EInputType.Textarea}
              type={'textarea'}
              placeholder={intl.formatMessage({
                id: 'ec.modals.import.manualInputPlaceholder',
                defaultMessage: "Produkt-ID's hier eingeben",
              })}
              value={idsRaw}
              rows={5}
              disabled={Boolean(file)}
              onChange={(name, value) => setIdsRaw(value)}
            />
            <div className='d-flex flex-column align-items-left mt-2'>
              <p>
                <InfoIcon className={'me-3 info-icon'} />
                <FormattedMessage
                  id='ec.modals.import.infoMessage.maximumIdsLimit'
                  defaultMessage="Sie können nur maximal 250 Produkt-ID's eingeben."
                />
              </p>
            </div>
          </React.Fragment>
        );
      case STEPS.InvalidIds:
        return (
          <React.Fragment>
            <div className='font-20 font-bold grey-border d-flex flex-column justify-content-center align-items-center py-48'>
              <WarningIcon />
              <div className='font-20 fw-bold mt-4'>
                <FormattedMessage
                  id='ec.modals.import.invalidIdsMessage'
                  defaultMessage="Sieht so aus, als gäbe es ein Problem mit Ihren eingegebenen Produkt-ID's"
                />
              </div>

              <div className='position-relative mt-4'>
                <div className='color-red font-bold  info-modal-trigger'>
                  <FormattedMessage
                    id='ec.modals.import.infoMessage.maximumIdsLimit'
                    defaultMessage="Sie können nur maximal 250 Produkt-ID's eingeben."
                  />
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      case STEPS.InvalidFile:
        return (
          <React.Fragment>
            <div className='font-20 font-bold grey-border d-flex flex-column justify-content-center align-items-center py-48 position-relative'>
              <WarningIcon />
              <div className='font-20 fw-bold mt-4'>
                <FormattedMessage
                  id='ec.modals.import.invalidFileMessage'
                  defaultMessage='Sieht aus, als gäbe es ein Problem mit Ihrer .csv-Datei'
                />
              </div>
              {getInfoModal()}
            </div>
          </React.Fragment>
        );
      case STEPS.Success:
        return (
          <React.Fragment>
            <div className='font-20 font-bold grey-border d-flex flex-column justify-content-center align-items-center py-48'>
              <ThumbsUpIcon />
              <div className='font-20 fw-bold mt-4'>
                <FormattedMessage
                  id='ec.modals.import.successMessage'
                  defaultMessage="Erfolgreich hinzugefügt {idsCount} Produkt-ID's"
                  values={{ idsCount: uploadedCounter }}
                />
              </div>
            </div>
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={classNames('importModal', {
        modalLoading: isLoading,
      })}
      id='root'
    >
      <div
        className={classNames(
          'modalHeader d-flex justify-content-between align-items-start border-bottom h-auto w-100',
        )}
      >
        <div className='import-header'>
          <FormattedMessage id='ec.modals.import.header' defaultMessage="Produkt-ID's importieren" />
        </div>
        <div className='close-modal align-self-start'>
          <Button
            onClick={() => {
              reset();
              navigate({ search: '' });
            }}
            className='mt-0'
          >
            <RedxIcon />
          </Button>
        </div>
      </div>
      <div className={classNames('modalBody h-100 position-relative')}>
        {isLoading ? (
          <div className='spinner grey-border'>
            <Spinner relative />
          </div>
        ) : (
          getStepContent()
        )}
      </div>
      <div
        className={classNames('modalFooter border-top d-flex w-100 h-auto bg-white', {
          'justify-content-end': !isMobile,
          'justify-content-between': isMobile,
        })}
      >
        {step !== STEPS.Success && (
          <Button
            buttonType={EButtonType.BasicLink}
            className='mt-0 me-24'
            onClick={() => {
              reset();
              navigate({ search: '' });
            }}
          >
            <FormattedMessage id='ec.components.buttons.cancel' defaultMessage='Abbrechen' />
          </Button>
        )}
        {getNextStepButton()}
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(actions as any, dispatch),
    actionsJourney1: bindActionCreators(_actionsJourney1 as any, dispatch),
    actionsJourney2: bindActionCreators(actionsJourney2 as any, dispatch),
    actionsJourney3: bindActionCreators(actionsJourney3 as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    actionsSearch: bindActionCreators(_actionsSearch as any, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchImportModal);
